import React, {useId, useState} from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import PropTypes from 'prop-types'
import {Box, Flex, useBreakpointValue} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'
import PDCTitle from '../../../components/pdc-title'
import {useIntl} from 'react-intl'
import Carousel from '../../../components/carousel'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {ArrowButton} from '../../../components/arrow-button/index.jsx'
import {generateResponsivePaddingRightForSlider} from '../../../utils/utils.js'

/**
 * PDC Home Home Categories Push component
 * @param {string} title - Title of the component
 * @param {string} titleHeadingLevel - Title heading of the component
 * @param {number} smallNbSlidesToShow - Number of slides to show on small screens
 * @param {number} mediumNbSlidesToShow - Number of slides to show on medium screens
 * @param {number} largeNbSlidesToShow - Number of slides to show on large screens
 * @param {number} xlargeNbSlidesToShow - Number of slides to show on xlarge screens
 * @param {number} xxlargeNbSlidesToShow - Number of slides to show on xxlarge screens
 * @param {object} regions - Regions
 * @returns {JSX.Element} - PDC Home Home Categories Push component
 */
export const PDCHomeCategoriesPush = ({
    title,
    titleHeadingLevel,
    smallNbSlidesToShow,
    mediumNbSlidesToShow,
    largeNbSlidesToShow,
    xlargeNbSlidesToShow,
    xxlargeNbSlidesToShow,
    displayPriority,
    regions
}) => {
    const intl = useIntl()
    const [swiper, setSwiper] = useState()

    // Get the tiles from the regions
    let tiles = []
    if (regions) {
        tiles = regions[0].components || []
    }

    // Get the number of slides to show depending on the screen size
    const nbSlidesToShowByBreakpoint = usePDCResponsiveElement({
        smallEl: smallNbSlidesToShow || 1,
        mediumEl: mediumNbSlidesToShow || 2,
        largeEl: largeNbSlidesToShow,
        xlargeEl: xlargeNbSlidesToShow,
        xxlargeEl: xxlargeNbSlidesToShow
    })
    const currentNbSlidesToShow = useBreakpointValue(nbSlidesToShowByBreakpoint)

    const titleId = useId()

    const carouselSettings = {
        dots: false,
        arrows: true,
        infinite: true,
        speed: 500,
        slidesToShow: currentNbSlidesToShow,
        prevArrow: <ArrowButton direction="prev" onClick={() => swiper?.slickPrev()} />,
        nextArrow: <ArrowButton direction="next" onClick={() => swiper?.slickNext()} />
    }

    return (
        <Box
            as="section"
            aria-describedby={titleId}
            css={[PDCHomeCategoriesPush.css(nbSlidesToShowByBreakpoint)]}
        >
            <PDCTitle
                title={title}
                titleHeadingLevel={titleHeadingLevel}
                id={titleId}
                className={'sr-only'}
            />
            {/* Display a carousel only if there are more tiles than the number of slides to show */}
            {tiles && tiles.length > currentNbSlidesToShow ? (
                <Carousel
                    settings={carouselSettings}
                    label={intl.formatMessage({
                        id: 'home.categories.push.carousel.label',
                        defaultMessage: 'Home categories push carousel'
                    })}
                    setSwiper={setSwiper}
                    nbSlidesToShowByBreakpoint={nbSlidesToShowByBreakpoint}
                >
                    {tiles.map((tile, index) => {
                        tile.displayPriority = displayPriority
                        return <Component key={tile?.id || index} component={tile} />
                    })}
                </Carousel>
            ) : (
                <Flex as="ul" listStyleType="none" display="flex" justifyContent="center">
                    {tiles.map((tile, index) => {
                        tile.nbSlides = currentNbSlidesToShow
                        tile.displayPriority = displayPriority
                        return (
                            <li key={tile?.id || index}>
                                <Component component={tile} />
                            </li>
                        )
                    })}
                </Flex>
            )}
        </Box>
    )
}

PDCHomeCategoriesPush.css = (nbSlidesToShow) => css`
    .slick-slider {
        padding-right: min(calc(77px), calc(100vw / 3));
    }
    .slick-list {
        overflow: visible;
    }
    .slick-arrow {
        opacity: 0;
        pointer-events: none;
        position: absolute;
        left: auto;
        right: 86px;
        bottom: 40px;
        top: auto;
        width: 40px;
        height: 40px;
        background: white;
        z-index: 1;
        transform: none;
        color: var(--chakra-colors-fullBlack);
        border: 1px solid var(--chakra-colors-gray);
        &::before {
            content: '';
        }

        @media screen and (min-width: 768px) {
            opacity: 1;
            pointer-events: auto;
        }
    }
    .slick-next {
        right: 40px;
    }

    ${generateResponsivePaddingRightForSlider(140, nbSlidesToShow, 3)}
`

PDCHomeCategoriesPush.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    smallNbSlidesToShow: PropTypes.number,
    mediumNbSlidesToShow: PropTypes.number,
    largeNbSlidesToShow: PropTypes.number,
    xlargeNbSlidesToShow: PropTypes.number,
    xxlargeNbSlidesToShow: PropTypes.number,
    displayPriority: PropTypes.bool,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
